import * as React from 'react';
import { useAuthStore } from '@/auth';
import { getProfileSkillLevelsOptions } from '../services/get-profile-skill-levels';
import { getInstrumentsOptions } from '../services/get-instruments';
import { InstrumentsLoading, useInstrumentsStore } from '../store/instruments-store';
import { getSkillLevelsOptions } from '../services/get-skill-levels';
import { Instruments } from '@/shared/i18n';
import { createProfileSkillLevel } from '../services/create-profile-skill-level';
import { DeleteProfileSkillLevelSchema, ProfileSkillLevelSchema } from '../types';
import { updateProfileSkillLevel } from '../services/update-profile-skill-level';
import { deleteProfileSkillLevel } from '../services/delete-profile-skill-level';
import { ActionFeedbackType } from '../../ui/shared/module-actions-feedback';
import { isLeft, isRight } from '@shared-commons/utils';
import { Auth } from 'firebase/auth';

export const useInstruments = (auth: Auth, dict: Instruments) => {
  const { user } = useAuthStore();
  const {
    instruments,
    setInstruments,
    setProfileSkillLevels,
    profileSkillLevels,
    skillLevelsOptions,
    setSkillLevelsOptions,
    setActionFeedback,
    setError,
    setIsLoading,
    resetStore,
  } = useInstrumentsStore();

  const messageMap = React.useMemo(
    () =>
      ({
        success: 'Operazione completata con successo',
        danger: "Si è verificato un errore durante l'operazione",
      }) as Partial<Record<ActionFeedbackType, string>>,
    [],
  );

  const refreshInstruments = React.useCallback(async () => {
    setError(null);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getInstrumentsOptions(accessToken)();
    if (isRight(result)) {
      setInstruments(result.value);
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict.instruments.instruments_refresh_error_message,
      );
      setError(error);
      setIsLoading(null);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [auth, setInstruments, dict, setIsLoading, setActionFeedback, messageMap, setError]);

  const refreshSkillLevels = React.useCallback(async () => {
    setError(null);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getSkillLevelsOptions(accessToken)();
    if (isRight(result)) {
      setSkillLevelsOptions(result.value);
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict.instruments.instruments_profile_refresh_error_message,
      );
      setError(error);
      setIsLoading(null);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [auth, setSkillLevelsOptions, dict, setIsLoading, setActionFeedback, messageMap, setError]);

  const refreshProfileSkillLevels = React.useCallback(async () => {
    setError(null);
    const accessToken = await auth.currentUser!.getIdToken(true);
    const result = await getProfileSkillLevelsOptions(user?.email, accessToken)();
    if (isRight(result)) {
      const output = result.value;
      setProfileSkillLevels(output);
    } else if (isLeft(result)) {
      const error = new Error(
        result.value?.message || dict.instruments.instruments_update_error_message,
      );
      setError(error);
      setIsLoading(null);
      setActionFeedback({
        message: messageMap?.['danger'],
        type: 'danger',
      });
    }
  }, [
    auth,
    user?.email,
    setProfileSkillLevels,
    dict,
    setIsLoading,
    setActionFeedback,
    messageMap,
    setError,
  ]);

  const addProfileSkillLevel = React.useCallback(
    async ({ data, onSuccess }: { data: ProfileSkillLevelSchema; onSuccess: () => void }) => {
      setError(null);
      setIsLoading(InstrumentsLoading.CREATE);
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await createProfileSkillLevel(user?.email, accessToken, { ...data })();
      if (isRight(result)) {
        onSuccess();
        setIsLoading(null);
        setError(null);
        setActionFeedback({
          message: messageMap?.['success'],
          type: 'success',
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante il recupero del profilo');
        setError(error);
        setIsLoading(null);
        setActionFeedback({
          message: messageMap?.['danger'],
          type: 'danger',
        });
      }
    },
    [auth, user?.email, setIsLoading, setActionFeedback, messageMap, setError],
  );

  const profileSkillLevelUpdate = React.useCallback(
    async ({ data, onSuccess }: { data: ProfileSkillLevelSchema; onSuccess: () => void }) => {
      setError(null);
      setIsLoading(InstrumentsLoading.UPDATE);
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await updateProfileSkillLevel(user?.email, accessToken, { ...data })();
      if (isRight(result)) {
        onSuccess();
        setIsLoading(null);
        setError(null);
        setActionFeedback({
          message: messageMap?.['success'],
          type: 'success',
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante il recupero del profilo');
        setError(error);
        setIsLoading(null);
        setActionFeedback({
          message: messageMap?.['danger'],
          type: 'danger',
        });
      }
    },
    [auth, user?.email, setIsLoading, setActionFeedback, messageMap, setError],
  );

  const profileSkillLevelDelete = React.useCallback(
    async ({ data, onSuccess }: { data: DeleteProfileSkillLevelSchema; onSuccess: () => void }) => {
      setError(null);
      setIsLoading(InstrumentsLoading.DELETE);
      const accessToken = await auth.currentUser!.getIdToken(true);
      const result = await deleteProfileSkillLevel(user?.email, accessToken, { ...data })();
      if (isRight(result)) {
        onSuccess();
        setIsLoading(null);
        setError(null);
        setActionFeedback({
          message: messageMap?.['success'],
          type: 'success',
        });
      } else if (isLeft(result)) {
        const error = new Error(result.value?.message || 'Errore durante il recupero del profilo');
        setError(error);
        setIsLoading(null);
        setActionFeedback({
          message: messageMap?.['danger'],
          type: 'danger',
        });
      }
    },
    [auth, user?.email, setIsLoading, setActionFeedback, messageMap, setError],
  );

  return {
    instruments,
    profileSkillLevels,
    skillLevelsOptions,
    refreshInstruments,
    refreshProfileSkillLevels,
    refreshSkillLevels,
    addProfileSkillLevel,
    profileSkillLevelUpdate,
    profileSkillLevelDelete,
    resetStore,
  };
};
